import {_, React, observer, observable, computed, IDisposer, action, useLocalStore, useEffect, useLayoutEffect, classNames, useAsObservableSource, utils, Fragment, Popover2, toJS, styled, pluralize} from '@api'
import {Button, ButtonGroup, IconName, Menu, MenuItem} from '@blueprintjs/core'
import {Tooltip} from '@material-ui/core'
import {Crop, FormatColorText, Palette, RecentActors, TextFormat, ViewColumn, Wallpaper} from '@material-ui/icons'
import {CardSizeSlider, useVipLocalStore, VipLocalStore} from '@modules'
import {AlignStyle, availableShapeColors, CatalogItemCardShape, CatalogItemShapeStore, FrameShapeStore, ChooseShapePopoverContent, ImageShape, TDBaseShape, TldrawApp, useTldrawApp, WhiteboardContextMenu, WhiteboardShape, validBackgroundColorShapesToStyle, validTextColorShapesToStyle, validStrokeShapesToStyle, TDShapeUtil, TDShape} from '@modules/whiteboard'
import {icons, StoresInstance} from '@stores'
import Vec from '@tldraw/vec'
import {guid} from '@utils'
import {PrimaryIconButton} from 'centric-ui-components'
import {autorun} from 'mobx'
import {ChangeEvent, useContext} from 'react'
import {shapeIcons, StyleMenu} from '~components/index'
import {TLBounds, Utils} from '@tldraw/core'
import * as css from './SelectedShapesFloatingToolbar.css'
import {CARD_CREATE_SIZE_SCALING_FACTOR} from './WhiteboardCreateShapesStore'

/// region Store

export class SelectedShapesFloatingToolbarStore extends VipLocalStore {
  constructor(stores: StoresInstance, public props: Omit<MyProps, 'className'> & { tldraw: TldrawApp }) {
    super(stores)
  }

  registerEffects = () => {
    const _toDispose: Array<IDisposer> = []
    const {tldraw} = this
    tldraw.toolbar = this

    return () => {
      if (tldraw.toolbar == this) tldraw.toolbar = null
      _toDispose.forEach(d => d())
    }
  }

  get tldraw() { return this.props.tldraw}

  @action deleteSelectedShapes = () => {
    this.tldraw.deleteShapes()
  }

  @action toggleLockShapes = () => {
    const {tldraw: {areAllSelectedShapesLocked, selectedShapes}} = this,
      newValue = !areAllSelectedShapesLocked

    selectedShapes.forEach(s => s.isLocked = newValue)
  }

  @computed get uniqueSelectedFontSizes() {
    const {tldraw: {selectedTextualShapes}} = this
    return _.uniq(selectedTextualShapes.map(s => s.style.fontSize).filter(_.identity))
  }

  @action applyFontSize = (size: number) => () => {
    this.tldraw.selectedTextualShapes.forEach(s => s.style.fontSize = size)
  }

  @computed get toolbarPosition() {
    const {tldraw: {domContainerRect, selectedBoundsScreen}} = this
    return (Number(selectedBoundsScreen?.selectionTop) - Number(domContainerRect?.top)) < 200 ? 'bottom' : 'top'
  }

  @computed get tooltipPosition() {
    return this.toolbarPosition
  }
}

export const SelectedShapesFloatingToolbarStoreContext = React.createContext<SelectedShapesFloatingToolbarStore>(null)
export const useSelectedShapesFloatingToolbarStore = () => React.useContext(SelectedShapesFloatingToolbarStoreContext)

/// endregion

/// region Components

interface MyProps {
  className?: string
}

export const SelectedShapesFloatingToolbar = observer<MyProps>(function SelectedShapesFloatingToolbar({className, ...props}) {
  const tldraw = useTldrawApp(),
    obsProps = useAsObservableSource({...props, tldraw}),
    store = useVipLocalStore((stores) => new SelectedShapesFloatingToolbarStore(stores, obsProps)),
    {toggleLockShapes, deleteSelectedShapes, toolbarPosition, tooltipPosition, tldraw: {tab}} = store, {selectedBoundsScreen, selectedShapes, areAllSelectedShapesLocked, store: root} = tldraw

  useEffect(() => store.registerEffects(), [])

  if (!selectedBoundsScreen || tldraw.currentTool !== tldraw.tools.tools.select) return null

  return (
    <SelectedShapesFloatingToolbarStoreContext.Provider value={store}>
      <Group className={css.root}
             data-pos={toolbarPosition}
             onMouseDown={root.setAsActiveTab}
        // onPointerDown={utils.eatMouseEvent}
        // onPointerUp={utils.eatMouseEvent}
      >
        <div className={css.toolbar}>
          {selectedShapes.length === 1 ? <SpecificShapeToolbarItems shape={selectedShapes[0]}/>
                                       : <MultipleShapesToolbarItems key={selectedShapes.length} />}

          <SelectParentFrameToolbarItem/>

          <TextStylesToolbarItems/>

          <Group>
            <Tooltip title='Styles (Deprecated)' placement={tooltipPosition}>
              <StyleMenu inToolbar/>
            </Tooltip>
            <StrokeColorDropdown/>
            <TextColorDropdown/>
            <BackgroundColorDropdown/>
          </Group>

          <Group>
            <PrimaryIconButton tooltip={areAllSelectedShapesLocked ? 'Unlock' : 'Lock'} tooltipStyle='mui' tooltipPosition={tooltipPosition} icon={areAllSelectedShapesLocked ? 'lock' : 'unlock'} onClick={toggleLockShapes}/>
            <PrimaryIconButton tooltip={`Delete ${pluralize('Shape', selectedShapes.length)}`} tooltipStyle='mui' tooltipPosition={tooltipPosition} icon={'trash'} onClick={deleteSelectedShapes}/>
          </Group>

          <ShapesToolbarMoreButton/>
        </div>
      </Group>
    </SelectedShapesFloatingToolbarStoreContext.Provider>)
})

class SpecificShapeToolbarItemsStore {
  constructor(public props: { shape: WhiteboardShape, tldraw: TldrawApp }) {}

  get shape() { return this.props.shape}

  get tldraw() { return this.props.tldraw}

  get util() { return this.tldraw.shapeUtilFor(this.shape)}

  get toolbar() { return this.tldraw.toolbar}

  @observable foo

  @action onChangeShapeName = (e: ChangeEvent<HTMLInputElement>) => {
    this.shape.name = e.currentTarget.value
  }

  @action onInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.select()
  }

  @action toggleObjectFit = () => {
    const img = this.shape as ImageShape
    img.objectFit = img.objectFit === 'cover' ? 'contain' : 'cover'
  }

  get shapeUtil() {
    return this.tldraw.shapeUtilFor(this.shape)
  }

  @computed get shapeStore() {
    const {shape: {id}, tldraw: {shapeComponentStores}} = this
    return shapeComponentStores[id]
  }

  @computed get currentShapeIcon(): IconName {
    const {shape: {type}} = this

    return shapeIcons[type] ?? 'shapes'
  }

  @action onToggleOptionalTitle = () => {
    const {shape} = this
    if (!shape.optionalTitle) {
      shape.optionalTitle = {
        show: true,
        title: ''
      }
    } else {
      shape.optionalTitle.show = !shape.optionalTitle.show
    }
  }
}

export const SpecificShapeToolbarItemsStoreContext = React.createContext<SpecificShapeToolbarItemsStore>(undefined)
export const useSpecificShapeToolbarItemsStore = () => useContext(SpecificShapeToolbarItemsStoreContext)

export const SpecificShapeToolbarItems = observer<{ shape: WhiteboardShape }>(function SpecificShapeToolbarItems({shape}) {
  const tldraw = useTldrawApp(),
    obsProps = useAsObservableSource({shape, tldraw}),
    store = useLocalStore(() => new SpecificShapeToolbarItemsStore(obsProps)),
    {onChangeShapeName, onInputFocus, toggleObjectFit, toolbar: {tooltipPosition}, shapeUtil: {tooltip}, tldraw: {imageUrlFor}} = store

  switch (shape.type) {
    case 'image':
      return <SpecificShapeToolbarItemsStoreContext.Provider value={store}>
        <Group>
          <div className={css.imageFilename}>
            <input type='text' value={shape.name} onChange={onChangeShapeName} onFocus={onInputFocus} style={{width: `${_.size(shape.name) * 10}px`}}/>
          </div>
        </Group>
        <Group>
          <PrimaryIconButton icon='download' tooltipStyle='mui' tooltip='Download' tooltipPosition={tooltipPosition} href={imageUrlFor(shape)} target='_blank' download={shape.name}/>
          {false && <PrimaryIconButton icon={<Crop/>} tooltipStyle='mui' tooltip='Crop' tooltipPosition={tooltipPosition}/>}
          <PrimaryIconButton icon='image-rotate-right' tooltipStyle='mui' tooltip='Object Fit' active={shape.objectFit === 'cover'} tooltipPosition={tooltipPosition} onClick={toggleObjectFit}/>
        </Group>
        <OptionalTitleToolbarItems/>
      </SpecificShapeToolbarItemsStoreContext.Provider>

    case 'catalogItem':
      return <SpecificShapeToolbarItemsStoreContext.Provider value={store}>
        <CatalogItemShapeToolbarItems/>
        <OptionalTitleToolbarItems/>
      </SpecificShapeToolbarItemsStoreContext.Provider>

    case 'frame':
      return <SpecificShapeToolbarItemsStoreContext.Provider value={store}>
        <FrameShapeToolbarItems/>
      </SpecificShapeToolbarItemsStoreContext.Provider>

    default:
      return <SpecificShapeToolbarItemsStoreContext.Provider value={store}>
        <SwitchCurrentShapeToolbarItem/>
      </SpecificShapeToolbarItemsStoreContext.Provider>
  }
})

export const OptionalTitleToolbarItems = observer(function OptionalTitleToolbarItems() {
  const {shape: {type, optionalTitle}, tldraw: {toolbar: {tooltipPosition}}, onToggleOptionalTitle} = useSpecificShapeToolbarItemsStore()

  if (!validTextColorShapesToStyle.has(type)) return null

  return (<Group>
      <PrimaryIconButton icon='header' tooltipStyle='mui' tooltipPosition={tooltipPosition} tooltip='Show Title?' active={optionalTitle?.show} onClick={onToggleOptionalTitle}/>
    </Group>
  )
})

export const SwitchCurrentShapeToolbarItem = observer(function SwitchCurrentShapeToolbarItem() {
  const {shape, currentShapeIcon, toolbar: {tooltipPosition}, shapeUtil: {tooltip}} = useSpecificShapeToolbarItemsStore()

  return (
    <Group>
      <Popover2 position='bottom' interactionKind='click' content={<ChooseShapePopoverContent shape={shape}/>}>
        <PrimaryIconButton icon={currentShapeIcon} tooltipStyle='mui' tooltip={tooltip} tooltipPosition={tooltipPosition}/>
      </Popover2>
    </Group>
  )
})

class CatalogItemShapeToolbarItemsStore {
  constructor(public store: SpecificShapeToolbarItemsStore) {}

  get shape(): CatalogItemCardShape { return this.store.shape as CatalogItemCardShape}

  get shapeStore(): CatalogItemShapeStore { return this.store.shapeStore as CatalogItemShapeStore }

  @action onToggleCatalogItemCardView = () => {
    const {shape, shape: {cardData}, shapeStore: {card}} = this

    if (cardData.view === 'builderCard') {
      _.merge(cardData, {sizes: {builderCardSize: _.cloneDeep(shape.size)}})
    } else {
      _.merge(cardData, {sizes: {listPreviewSize: _.cloneDeep(shape.size)}})
    }

    cardData.view = cardData.view === 'builderCard' ? 'listPreviewCard' : 'builderCard'
    if (cardData.view === 'builderCard') {
      shape.size = cardData.sizes?.builderCardSize ?? [card.prototypicalCardSizeWhiteboard.width * CARD_CREATE_SIZE_SCALING_FACTOR, card.prototypicalCardSizeWhiteboard.height * CARD_CREATE_SIZE_SCALING_FACTOR]
    } else {
      shape.size = cardData.sizes?.listPreviewSize ?? [300, 100]
      return undefined
    }
  }

  @observable foo = false
}

export const FrameShapeToolbarItems = observer(function FrameShapeToolbarItems() {
  const parent = useSpecificShapeToolbarItemsStore(),
    shapeStore = parent.shapeStore as FrameShapeStore

  if (!shapeStore) return null

  const {onSelectContentShapes, shapesContainedBy} = shapeStore,
    {toolbar: {tooltipPosition}} = parent
  return (
    <Fragment>
      <SwitchCurrentShapeToolbarItem/>

      <Group>
        <PrimaryIconButton icon='multi-select' tooltipStyle='mui' tooltip={`Select Contents (${shapesContainedBy.length})`} tooltipPosition={tooltipPosition} onClick={onSelectContentShapes}/>
      </Group>
    </Fragment>)
})

export const CatalogItemShapeToolbarItems = observer(function CatalogItemShapeToolbarItems() {
  const parent = useSpecificShapeToolbarItemsStore(),
    {onToggleCatalogItemCardView, store: {toolbar: {tooltipPosition}}, shape: {cardData: {showDetailsPopover, view}}, shapeStore} = useLocalStore(() => new CatalogItemShapeToolbarItemsStore(parent))

  if (!shapeStore?.card) return null

  const {toggleShowPopover, title, strings, card} = shapeStore

  const {workspace: {itemName}, openCatalogItemDetails, navigateToSeason} = card

  return (
    <Fragment>
      <Group>
        <PrimaryIconButton icon='mugshot' tooltipStyle='mui' tooltip='Change View' tooltipPosition={tooltipPosition} onClick={onToggleCatalogItemCardView}/>
      </Group>
      <Group>
        <span className={css.catalogItemTitle}>{title}</span>
      </Group>
      <Group>
        <PrimaryIconButton icon={'properties'} tooltipStyle='mui' tooltipPosition={tooltipPosition} tooltip={showDetailsPopover ? '' : 'Show Popover'} active={showDetailsPopover} onClick={toggleShowPopover}/>
        <PrimaryIconButton icon={icons.inlineDetails} tooltipStyle='mui' tooltipPosition={tooltipPosition}
                           tooltip={`${_.capitalize(itemName)}
                            ${_.capitalize(strings.details)}`} onClick={openCatalogItemDetails}/>
        {false && <Tooltip placement={tooltipPosition} title={`Navigate to Season`}>
			<PrimaryIconButton icon={icons.goToSeason} onClick={navigateToSeason}/>
		</Tooltip>}
      </Group>
    </Fragment>)
})

const Group = styled.div.attrs<{ className?: string }>(p => ({className: classNames(css.group, p.className)}))``

class MultipleShapesToolbarItemsStore {
  constructor(public tldraw: TldrawApp) {
    const {tldraw: {selectedShapes, selectedBounds, shapeUtil: {getCenter, getBounds}}} = this
    this.originalSelectedBounds = _.cloneDeep(selectedBounds)
    this.originalSelectedBoundsCenter = Utils.getBoundsCenter(selectedBounds)
    this.initialShapes = selectedShapes.map(s => {
      const center = getCenter(s)

      return ({shape: s, bounds: getBounds(s), center, distanceFromCenter: Vec.sub(this.originalSelectedBoundsCenter, center)})
    })
  }

  initialShapes: Array<{ shape: TDShape, bounds: TLBounds, center: number[], distanceFromCenter: number[] }>
  private originalSelectedBounds: TLBounds
  private originalSelectedBoundsCenter: number[]

  /**
   * Spread 0 = all cards should be at the same final center point (center of original bounds)
   * Spread 200 = all cards should be
   * @param e
   * @param v
   */
  @action updateShapeSpread = (e, v: number) => {
    this.spread = v
    // console.debug(`updateShapeSpread`, v)
    const {tldraw: {selectedShapes, shapeUtil: {transform}}, initialShapes} = this

    let multiplier = (100 - v) / 100
    if (v > 100) {
      multiplier /= 2
    }

    selectedShapes.forEach((s, i) => {
      const {center, distanceFromCenter, bounds} = initialShapes[i]
      s.point[0] = bounds.minX + (multiplier * distanceFromCenter[0])
    })

  }

  @observable spread = 100
}

export const MultipleShapesToolbarItems = observer(function MultipleShapesToolbarItems() {
  const tldraw = useTldrawApp(),
    store = useLocalStore(() => new MultipleShapesToolbarItemsStore(tldraw)),
    {
      spread, updateShapeSpread,
      tldraw: {selectedShapes, toolbar: {tooltipPosition}}
    } = store

  return (<Fragment>
    <Group>
      <Button minimal text={`${selectedShapes.length} ${pluralize(`Item`, selectedShapes.length)}`}/>
    </Group>

    <Group>
      <CardSizeSlider tooltipTitle='Spread' className={css.cardSpread} max={300} min={0} value={spread}
                      zoomOutIcon={<RecentActors/>} zoomInIcon={<ViewColumn/>} tooltipLocation={tooltipPosition}
                      onChange={updateShapeSpread}/>
    </Group>
  </Fragment>)
})

export const SelectParentFrameToolbarItem = observer(function SelectParentFrameToolbarItem() {
  const {myPageState, selectedShapesFrame, toolbar: {tooltipPosition}} = useTldrawApp()

  return (<Fragment>
    {selectedShapesFrame && <Group>
		<PrimaryIconButton icon='multi-select' tooltip={`Select Frame`} tooltipStyle='mui' tooltipPosition={tooltipPosition} onClick={() => myPageState.selectedIds = [selectedShapesFrame.id]}/>
	</Group>}
  </Fragment>)
})

export const TextStylesToolbarItems = observer(function TextStylesToolbarItems() {
  const {tldraw: {areAnyTextEnabledShapesSelected}} = useSelectedShapesFloatingToolbarStore()

  if (!areAnyTextEnabledShapesSelected) return null

  return (<Fragment>
    <Group>
      <FontSizeDropdown/>
    </Group>
    <Group>
      <FontStyleDropdown/>
      <TextAlignDropdown/>
    </Group>
  </Fragment>)
})

export const StrokeColorDropdown = observer(function TextColorDropdown() {
  const {selectedShapes, toolbar: {tooltipPosition}} = useTldrawApp(),
    shape = _.first(selectedShapes) as TDBaseShape

  if (!validStrokeShapesToStyle.has(shape?.type)) return null
  const {style} = shape

  return (
    <Popover2
      interactionKind='click'
      position={tooltipPosition}
      content={<ColorChooserPanel color={style.strokeColor} onChangeColor={color => style.strokeColor = color}/>}
    >
      <PrimaryIconButton
        style={{'--stroke-color': style.strokeColor} as any}
        className={css.strokeColorButton} tooltip='Color' tooltipPosition={tooltipPosition} tooltipStyle='mui' icon={<FormatColorText/>}/>
    </Popover2>)
})

export const TextColorDropdown = observer(function TextColorDropdown() {
  const {selectedShapes, toolbar: {tooltipPosition}} = useTldrawApp(),
    shape = _.first(selectedShapes) as TDBaseShape

  if (!validTextColorShapesToStyle.has(shape?.type)) return null
  const {style} = shape

  return (
    <Popover2
      interactionKind='click'
      position={tooltipPosition}
      content={<ColorChooserPanel color={style.textColor} onChangeColor={color => style.textColor = color}/>}
    >
      <PrimaryIconButton
        style={{'--text-color': style.textColor} as any}
        className={css.textColorButton} tooltip='Text Color' tooltipPosition={tooltipPosition} tooltipStyle='mui' icon={<FormatColorText/>}/>
    </Popover2>)
})

export const BackgroundColorDropdown = observer(function BackgroundColorDropdown() {
  const {selectedShapes, toolbar: {tooltipPosition}} = useTldrawApp(),
    shape = _.first(selectedShapes) as TDBaseShape

  if (!validBackgroundColorShapesToStyle.has(shape?.type)) return null

  const {style} = shape
  return (
    <Popover2
      interactionKind='click'
      position={tooltipPosition}
      content={<ColorChooserPanel includeTransparent color={style.backgroundColor} onChangeColor={color => style.backgroundColor = color}/>}
    >
      <PrimaryIconButton style={{'--background-color': style.backgroundColor && style.backgroundColor != 'transparent' ? style.backgroundColor : undefined} as any} className={css.backgroundColorButton} tooltip='Background Color' tooltipPosition={tooltipPosition} tooltipStyle='mui' icon={<Wallpaper/>}/>
    </Popover2>)
})

export const ColorChooserPanel = observer<{ color, onChangeColor, includeTransparent?: boolean }>(function ColorChooserPanel({color, onChangeColor, includeTransparent}) {
  return (<div className={css.colorPanel}>
    {includeTransparent && <div className={css.availableColor}
	                            data-transparent
	                            data-selected={!color || 'transparent' === color}
	                            style={{'--color': 'transparent'} as any}
	                            onClick={() => onChangeColor('transparent')}/>}
    {availableShapeColors.map((c, i) =>
      <div className={css.availableColor}
           key={i}
           data-selected={c === color}
           style={{'--color': c} as any}
           onClick={() => onChangeColor(c)}/>)}
  </div>)
})

export const availableFontSizes = [12, 14, 18, 24, 32, 48, 64, 128, 256]

export const FontSizeDropdown = observer(function FontSizeDropdown() {
  const {uniqueSelectedFontSizes, applyFontSize} = useSelectedShapesFloatingToolbarStore()

  return (
    <Popover2
      interactionKind='click'
      position='bottom'
      content={<Menu>
        <MenuItem
          text={'Auto'}
          onClick={applyFontSize(null)}/>
        {availableFontSizes.map(size => (
          <MenuItem
            key={size}
            text={size}
            onClick={applyFontSize(size)}/>
        ))}
      </Menu>}
    >
      <Tooltip title='Font Size' placement='top'>
        <Button rightIcon='double-caret-vertical'
                text={_.isEmpty(uniqueSelectedFontSizes) ? 'Auto' : uniqueSelectedFontSizes.length == 1 ? uniqueSelectedFontSizes[0] : '...'}
                minimal/>
      </Tooltip>
    </Popover2>)
})

class FontStyleDropdownStore {
  constructor(public tldraw: TldrawApp) {}

  @action onToggleBold = () => {
    const {tldraw: {selectedShapes}, boldActive} = this
    selectedShapes.forEach(s => _.merge(s, {style: {fontStyle: {bold: !boldActive}}}))
  }

  @action onToggleItalic = () => {
    const {tldraw: {selectedShapes}, italicActive} = this
    selectedShapes.forEach(s => _.merge(s, {style: {fontStyle: {italic: !italicActive}}}))
  }

  @action onToggleUnderline = () => {
    const {tldraw: {selectedShapes}, underlineActive} = this
    selectedShapes.forEach(s => _.merge(s, {style: {fontStyle: {underline: !underlineActive}}}))
  }

  @action onToggleStrikethrough = () => {
    const {tldraw: {selectedShapes}, strikethroughActive} = this
    selectedShapes.forEach(s => _.merge(s, {style: {fontStyle: {strikethrough: !strikethroughActive}}}))
  }

  @computed get boldActive() {
    const {tldraw: {selectedShapes}} = this
    return selectedShapes.every(s => s.style?.fontStyle?.bold)
  }

  @computed get italicActive() {
    const {tldraw: {selectedShapes}} = this
    return selectedShapes.every(s => s.style?.fontStyle?.italic)
  }

  @computed get underlineActive() {
    const {tldraw: {selectedShapes}} = this
    return selectedShapes.every(s => s.style?.fontStyle?.underline)
  }

  @computed get strikethroughActive() {
    const {tldraw: {selectedShapes}} = this
    return selectedShapes.every(s => s.style?.fontStyle?.strikethrough)
  }
}

export const FontStyleDropdown = observer(function FontStyleDropdown() {
  const tldraw = useTldrawApp(),
    {
      onToggleBold, onToggleItalic, onToggleUnderline, onToggleStrikethrough,
      strikethroughActive, italicActive, boldActive, underlineActive,
      tldraw: {selectedShapes, toolbar: {tooltipPosition}}
    } = useLocalStore(() => new FontStyleDropdownStore(tldraw))

  if (selectedShapes.length != 1) return null

  return (<Popover2
    interactionKind='click'
    position='bottom'
    content={
      <ButtonGroup minimal className={css.buttons}>
        <PrimaryIconButton icon='bold' tooltipStyle='mui' tooltipPosition='bottom' tooltip='Bold' active={boldActive} onClick={onToggleBold}/>
        <PrimaryIconButton icon='italic' tooltipStyle='mui' tooltipPosition='bottom' tooltip='Italic' active={italicActive} onClick={onToggleItalic}/>
        <PrimaryIconButton icon='underline' tooltipStyle='mui' tooltipPosition='bottom' tooltip='Underline' active={underlineActive} onClick={onToggleUnderline}/>
        <PrimaryIconButton icon='strikethrough' tooltipStyle='mui' tooltipPosition='bottom' tooltip='Strikethrough' active={strikethroughActive} onClick={onToggleStrikethrough}/>
      </ButtonGroup>
    }
  >
    <PrimaryIconButton icon={<TextFormat/>} tooltip='Font Style' tooltipStyle='mui' tooltipPosition={tooltipPosition}/>
  </Popover2>)
})

class FontAlignDropdownStore {
  constructor(public tldraw: TldrawApp) {}

  @computed get aligns() {
    const {tldraw: {selectedShapes}} = this
    return _.uniq(selectedShapes.map(s => s.style.textAlign))
  }

  @computed get alignIcon(): IconName {
    const {aligns, tldraw: {selectedShapes}} = this

    if (aligns.length === 1) {
      switch (_.first(aligns) as AlignStyle) {
        case 'start':
          return 'align-left'

        case 'justify':
          return 'align-justify'

        case 'middle':
          return 'align-center'

        case 'end':
          return 'align-right'

        default:
          break
      }
    }

    return 'align-left'
  }

  @computed get alignLeftActive() { return this.aligns.length === 1 && this.alignIcon === 'align-left'}

  @computed get alignRightActive() { return this.aligns.length === 1 && this.alignIcon === 'align-right'}

  @computed get alignCenterActive() { return this.aligns.length === 1 && this.alignIcon === 'align-center'}

  @computed get alignJustifyActive() { return this.aligns.length === 1 && this.alignIcon === 'align-justify'}

  @action onToggleAlignLeft = () => {
    this.tldraw.selectedShapes.forEach(s => s.style.textAlign = 'start')
  }

  @action onToggleAlignCenter = () => {
    this.tldraw.selectedShapes.forEach(s => s.style.textAlign = 'middle')
  }
  @action onToggleAlignRight = () => {
    this.tldraw.selectedShapes.forEach(s => s.style.textAlign = 'end')
  }
  @action onToggleAlignJustify = () => {
    this.tldraw.selectedShapes.forEach(s => s.style.textAlign = 'justify')
  }
}

export const TextAlignDropdown = observer(function TextAlignDropdown() {
  const tldraw = useTldrawApp(),
    {
      alignIcon, alignRightActive, alignLeftActive, alignJustifyActive, alignCenterActive,
      onToggleAlignLeft, onToggleAlignCenter, onToggleAlignRight, onToggleAlignJustify,
      tldraw: {selectedShapes, toolbar: {tooltipPosition}}
    } = useLocalStore(() => new FontAlignDropdownStore(tldraw))

  if (selectedShapes.length != 1) return null

  return (<Popover2
    interactionKind='click'
    position='bottom'
    content={
      <ButtonGroup minimal className={css.buttons}>
        <PrimaryIconButton icon='align-left' tooltipStyle='mui' active={alignLeftActive} onClick={onToggleAlignLeft}/>
        <PrimaryIconButton icon='align-center' tooltipStyle='mui' active={alignCenterActive} onClick={onToggleAlignCenter}/>
        <PrimaryIconButton icon='align-right' tooltipStyle='mui' active={alignRightActive} onClick={onToggleAlignRight}/>
        <PrimaryIconButton icon='align-justify' tooltipStyle='mui' active={alignJustifyActive} onClick={onToggleAlignJustify}/>
      </ButtonGroup>
    }
  >
    <PrimaryIconButton icon={alignIcon} tooltip='Alignment' tooltipPosition={tooltipPosition}/>
  </Popover2>)
})

/*
<PrimaryIconButton icon='align-left' tooltipPosition='bottom' tooltip='Left' active={alignLeftActive} onClick={onToggleAlignLeft}/>
        <PrimaryIconButton icon='align-center' tooltipPosition='bottom' tooltip='Center' active={alignCenterActive} onClick={onToggleAlignCenter}/>
        <PrimaryIconButton icon='align-right' tooltipPosition='bottom' tooltip='Right' active={alignRightActive} onClick={onToggleAlignRight}/>
        <PrimaryIconButton icon='align-justify' tooltipPosition='bottom' tooltip='Justify' active={alignJustifyActive} onClick={onToggleAlignJustify}/>
 */
/// endregion

export const ShapesToolbarMoreButton = observer(function ShapesToolbarMoreButton() {
  const {} = useSelectedShapesFloatingToolbarStore()

  return (<Fragment>
    <Group>
      <Popover2
        interactionKind='click'
        position='bottom'
        content={
          <Menu>
            <WhiteboardContextMenu/>
          </Menu>
        }
      >
        <PrimaryIconButton icon={'more'}/>
      </Popover2>
    </Group>
  </Fragment>)
})


// WEBPACK FOOTER //
// ./src/modules/whiteboard/SelectedShapesFloatingToolbar.tsx